"use client";

import { useEffect, useState } from "react";
import { Checkbox, Notice, TextField } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  DatamuseCredit,
  ExampleWords,
  LookupState,
  MeaningCard,
  revealField,
} from "@/components/tools/vocabulary/word-parts";
import {
  ConfusedSetList,
  HomophoneGroupCard,
} from "@/components/tools/word-knowledge/homophone-results";
import { t } from "@/lib/i18n/interpolate";
import {
  type PartOfSpeech,
  splitEcho,
} from "@/lib/tool-kit/vocabulary/datamuse";
import { arpabetToIpa } from "@/lib/tool-kit/vocabulary/ipa";
import { loadPronouncingDictionary } from "@/lib/tool-kit/vocabulary/pronouncing-dictionary";
import { useDatamuse } from "@/lib/tool-kit/vocabulary/use-datamuse";
import { COMMON_HOMOPHONES } from "@/lib/tool-kit/word-knowledge/common-homophones";
import {
  buildHomophoneGroups,
  type HomophoneIndex,
  homophoneQuery,
  loadHomophoneIndex,
  normalizeHomophoneQuery,
  wordOfHomophoneQuery,
} from "@/lib/tool-kit/word-knowledge/homophones";

/** English strings. A translated content file overrides these through `ui`. */
export const HOMOPHONE_FINDER_STRINGS = {
  wordLabel: "Word",
  wordPlaceholder: "bear",
  wordHint:
    "Results appear as you type. Click any result to look up its own homophones.",
  stressLabel: "Ignore stress differences (match the sounds only)",
  tryLabel: "Try:",
  examples: ["bear", "read", "their", "knight", "weather"],
  emptyPrompt:
    "Type a word to find the words that are pronounced exactly like it, with a definition for each.",
  loadingData: "Loading pronunciation data…",
  dataError:
    "Could not load the pronunciation data. Check your connection and try again.",
  heading: "Homophones of “{word}”",
  groupHeading: "Pronounced /{ipa}/",
  groupHeadingPlain: "Pronounced alike",
  arpabetNote: "CMU pronunciation: {arpabet}",
  variantNote:
    "“{word}” has more than one pronunciation, so it has a set of homophones for each.",
  uncommonSummary:
    "Names, letters and unusual spellings that sound the same ({count})",
  noHomophones:
    "No other word in the CMU Pronouncing Dictionary sounds exactly like “{word}”.",
  noHomophonesIpa:
    "“{word}” is pronounced /{ipa}/, and no other word in the CMU Pronouncing Dictionary sounds exactly like it. Try ignoring stress differences, or check the spelling.",
  notInDictionary:
    "“{word}” is not in the CMU Pronouncing Dictionary, so it has no pronunciation to match. Check the spelling, or try a base form — run rather than running.",
  posNames: {
    n: "noun",
    v: "verb",
    adj: "adjective",
    adv: "adverb",
    u: "other",
  } as Record<PartOfSpeech, string>,
  meaningLine: "{pos}: {definition}",
  accentNote:
    "Pronunciations are General American English from the CMU Pronouncing Dictionary. Two words that sound alike in your accent may be separate here, and the reverse.",
  confusedHeading: "Homophones people often confuse",
  confusedIntro:
    "Click a set to look it up. Each tip is a quick way to choose the right spelling.",
  confusedSets: COMMON_HOMOPHONES,
  searching: "Searching…",
  errorNetwork:
    "Could not reach the Datamuse word service, so definitions are missing. The matching words are still listed — check your connection and try again.",
  errorBusy:
    "Datamuse is receiving too many requests right now, so definitions are missing. Wait a minute and try again.",
  errorUnavailable:
    "The Datamuse word service did not answer, so definitions are missing. Try again in a moment.",
  retry: "Try again",
  copy: "Copy",
  copied: "Copied!",
  copyWord: "Copy “{word}”",
  credit: "Word data from the Datamuse API",
  sourceNote:
    "Pronunciations: CMU Pronouncing Dictionary, Carnegie Mellon University (BSD-style licence).",
} as const;

const FIELD_ID = "hom-word";
const MAX_INPUT = 40;
const RE_LETTER = /\p{L}/u;

interface IndexState {
  status: "idle" | "loading" | "ready" | "error";
  index: HomophoneIndex | null;
}

/** The homophone sets, fetched on the first search and kept for the life of the page. */
function useHomophoneIndex(
  enabled: boolean
): IndexState & { retry: () => void } {
  const [state, setState] = useState<IndexState>({
    status: "idle",
    index: null,
  });
  const [attempt, setAttempt] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` re-runs a failed load on demand
  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    setState((previous) =>
      previous.index ? previous : { status: "loading", index: null }
    );
    loadHomophoneIndex()
      .then((index) => {
        if (!cancelled) {
          setState({ status: "ready", index });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ status: "error", index: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, attempt]);

  return { ...state, retry: () => setAttempt((count) => count + 1) };
}

/** IPA for a word that has no homophones, from the converter's dictionary. */
function useOwnIpa(word: string, enabled: boolean): string | null {
  const [found, setFound] = useState<{ word: string; ipa: string | null }>({
    word: "",
    ipa: null,
  });
  useEffect(() => {
    if (!(enabled && word)) {
      return;
    }
    let cancelled = false;
    loadPronouncingDictionary()
      .then((dictionary) => {
        const arpabet = dictionary.lookup(word);
        if (!cancelled) {
          setFound({ word, ipa: arpabet ? arpabetToIpa(arpabet) : null });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFound({ word, ipa: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [word, enabled]);
  return found.word === word ? found.ipa : null;
}

export default function HomophoneFinderWidget() {
  const strings = useToolUi(HOMOPHONE_FINDER_STRINGS);
  const [input, setInput] = useState("");
  const [ignoreStress, setIgnoreStress] = useState(false);
  const normalized = normalizeHomophoneQuery(input);
  const word = RE_LETTER.test(normalized) ? normalized : "";
  const data = useHomophoneIndex(word !== "");
  const lookup = useDatamuse(word ? [homophoneQuery(word)] : []);
  const answeredWord = wordOfHomophoneQuery(lookup.answered);
  const answer =
    lookup.status === "ready" && answeredWord === word
      ? lookup.results[0]
      : null;
  const raw =
    word && data.index ? data.index.groupsFor(word, ignoreStress) : [];
  const groups = buildHomophoneGroups(raw, answer ?? null);
  const empty = data.status === "ready" && word !== "" && groups.length === 0;
  const ownIpa = useOwnIpa(word, empty);

  function lookUp(next: string) {
    setInput(next);
    revealField(FIELD_ID);
  }

  return (
    <>
      <TextField
        hint={strings.wordHint}
        id={FIELD_ID}
        label={strings.wordLabel}
        onChange={(value) => setInput(value.slice(0, MAX_INPUT))}
        placeholder={strings.wordPlaceholder}
        type="search"
        value={input}
      />
      <Checkbox
        checked={ignoreStress}
        label={strings.stressLabel}
        onChange={setIgnoreStress}
      />
      {word ? (
        <Status data={data} lookup={lookup} strings={strings} />
      ) : (
        <div className="space-y-3">
          <Notice>{strings.emptyPrompt}</Notice>
          <ExampleWords
            label={strings.tryLabel}
            onSelect={lookUp}
            words={strings.examples}
          />
        </div>
      )}
      {groups.length > 0 ? (
        <div aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{t(strings.heading, { word })}</h2>
          {answer ? (
            <MeaningCard
              echo={splitEcho(answer).echo}
              meaningLine={strings.meaningLine}
              posNames={strings.posNames}
            />
          ) : null}
          {groups.length > 1 ? (
            <p className={HINT}>{t(strings.variantNote, { word })}</p>
          ) : null}
          {groups.map((group) => (
            <HomophoneGroupCard
              group={group}
              key={group.pron}
              onSelect={lookUp}
              strings={strings}
            />
          ))}
          <p className={HINT}>{strings.accentNote}</p>
        </div>
      ) : null}
      {empty ? (
        <Notice>
          {ownIpa
            ? t(strings.noHomophonesIpa, { word, ipa: ownIpa })
            : t(strings.noHomophones, { word })}
        </Notice>
      ) : null}
      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.confusedHeading}</h2>
        <p className={HINT}>{strings.confusedIntro}</p>
        <ConfusedSetList onSelect={lookUp} sets={strings.confusedSets} />
      </section>
      <DatamuseCredit label={strings.credit} />
      <p className={HINT}>{strings.sourceNote}</p>
    </>
  );
}

function Status({
  data,
  lookup,
  strings,
}: {
  data: IndexState & { retry: () => void };
  lookup: ReturnType<typeof useDatamuse>;
  strings: typeof HOMOPHONE_FINDER_STRINGS;
}) {
  if (data.status === "error") {
    return (
      <div className="space-y-3">
        <Notice>{strings.dataError}</Notice>
        <button className="underline" onClick={data.retry} type="button">
          {strings.retry}
        </button>
      </div>
    );
  }
  if (data.status === "loading") {
    return (
      <output className="block text-muted-foreground text-sm">
        {strings.loadingData}
      </output>
    );
  }
  return (
    <LookupState
      failure={lookup.failure}
      onRetry={lookup.retry}
      status={lookup.status}
      strings={strings}
    />
  );
}
